export const HOME_SCREEN_EXPERIMENT_ID = "home-shortcut-v2";
export const HOME_SCREEN_EXPERIMENT_TEST_START_PARAMETER =
  "home_screen_shortcut_test";
export const HOME_SCREEN_EXPERIMENT_TREATMENT_PERCENT = 100;
export const HOME_SCREEN_EXPERIMENT_STARTS_AT = Date.parse(
  "2026-10-04T21:00:00Z",
);
export const HOME_SCREEN_PROMPT_MAX_SHOWS = 2;
export const HOME_SCREEN_PROMPT_SHOW_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1_000;
export const HOME_SCREEN_PROMPT_DISMISS_COOLDOWN_MS =
  14 * 24 * 60 * 60 * 1_000;
export const HOME_SCREEN_PROMPT_UNSUPPORTED_COOLDOWN_MS =
  30 * 24 * 60 * 60 * 1_000;

export type HomeScreenExperimentVariant = "off" | "control" | "treatment";

export interface HomeScreenPromptProgress {
  shownCount: number;
  dismissedUntil: number;
  unsupportedUntil: number;
  added: boolean;
}

const EMPTY_PROGRESS: HomeScreenPromptProgress = {
  shownCount: 0,
  dismissedUntil: 0,
  unsupportedUntil: 0,
  added: false,
};

export function homeScreenExperimentIsStarted(now = Date.now()): boolean {
  return now >= HOME_SCREEN_EXPERIMENT_STARTS_AT;
}

export function homeScreenExperimentVariant(options: {
  enabled: boolean;
  userId: string | null;
  startParameter: string | null;
}): HomeScreenExperimentVariant {
  if (
    options.startParameter === HOME_SCREEN_EXPERIMENT_TEST_START_PARAMETER
  ) {
    return "treatment";
  }
  if (!options.enabled || !options.userId) {
    return "off";
  }
  return "treatment";
}

export function homeScreenAutomaticEligible(options: {
  enabled: boolean;
  mobileTelegram: boolean;
  returningVisitor: boolean;
  usefulAction: boolean;
  progress: Readonly<HomeScreenPromptProgress>;
  now?: number;
}): boolean {
  return options.enabled && options.mobileTelegram && options.returningVisitor &&
    options.usefulAction && homeScreenPromptCanBeOffered(options.progress, options.now);
}

export function parseHomeScreenPromptProgress(
  value: string | null,
): HomeScreenPromptProgress {
  if (!value) {
    return { ...EMPTY_PROGRESS };
  }
  try {
    const parsed = JSON.parse(value) as Partial<HomeScreenPromptProgress>;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return { ...EMPTY_PROGRESS };
    }
    return {
      shownCount:
        typeof parsed.shownCount === "number" &&
        Number.isInteger(parsed.shownCount) &&
        parsed.shownCount >= 0
          ? Math.min(parsed.shownCount, HOME_SCREEN_PROMPT_MAX_SHOWS)
          : 0,
      dismissedUntil:
        typeof parsed.dismissedUntil === "number" &&
        Number.isFinite(parsed.dismissedUntil) &&
        parsed.dismissedUntil > 0
          ? parsed.dismissedUntil
          : 0,
      unsupportedUntil:
        typeof parsed.unsupportedUntil === "number" &&
        Number.isFinite(parsed.unsupportedUntil) &&
        parsed.unsupportedUntil > 0
          ? parsed.unsupportedUntil
          : 0,
      added: parsed.added === true,
    };
  } catch {
    return { ...EMPTY_PROGRESS };
  }
}

export function homeScreenPromptCanBeOffered(
  progress: Readonly<HomeScreenPromptProgress>,
  now = Date.now(),
): boolean {
  return (
    !progress.added &&
    progress.shownCount < HOME_SCREEN_PROMPT_MAX_SHOWS &&
    progress.dismissedUntil <= now &&
    progress.unsupportedUntil <= now
  );
}

export function markHomeScreenPromptShown(
  progress: Readonly<HomeScreenPromptProgress>,
  now = Date.now(),
): HomeScreenPromptProgress {
  return {
    ...progress,
    shownCount: Math.min(
      HOME_SCREEN_PROMPT_MAX_SHOWS,
      progress.shownCount + 1,
    ),
    // Closing Telegram without dismissing the sheet must not cause another
    // automatic offer on the very next launch. Explicit dismissal extends to 14d.
    dismissedUntil: Math.max(progress.dismissedUntil, now + HOME_SCREEN_PROMPT_SHOW_COOLDOWN_MS),
  };
}

export function dismissHomeScreenPromptProgress(
  progress: Readonly<HomeScreenPromptProgress>,
  now = Date.now(),
): HomeScreenPromptProgress {
  return {
    ...progress,
    dismissedUntil: now + HOME_SCREEN_PROMPT_DISMISS_COOLDOWN_MS,
  };
}

export function markHomeScreenUnsupported(
  progress: Readonly<HomeScreenPromptProgress>,
  now = Date.now(),
): HomeScreenPromptProgress {
  return {
    ...progress,
    unsupportedUntil: now + HOME_SCREEN_PROMPT_UNSUPPORTED_COOLDOWN_MS,
  };
}

export function markHomeScreenAdded(
  progress: Readonly<HomeScreenPromptProgress>,
): HomeScreenPromptProgress {
  return { ...progress, added: true };
}
